import { produce, type SetStoreFunction } from 'solid-js/store'
import type { NodeId, Scene, SceneNode } from './types.ts'

/** `null` means "this node did not exist at this point in time". */
export type NodeSnapshot = Record<NodeId, SceneNode | null>

export interface SceneDelta {
  nodes: NodeSnapshot
  order?: NodeId[]
}

export interface HistoryEntry {
  label: string
  before: SceneDelta
  after: SceneDelta
  at: number
}

export const HISTORY_LIMIT = 200

export function snapshotNodes(scene: Scene, ids: Iterable<NodeId>): NodeSnapshot {
  const out: NodeSnapshot = {}
  for (const id of ids) {
    const node = scene.nodes[id]
    out[id] = node ? structuredClone(node) : null
  }
  return out
}

export function snapshotDelta(
  scene: Scene,
  ids: Iterable<NodeId>,
  includeOrder: boolean,
): SceneDelta {
  const delta: SceneDelta = { nodes: snapshotNodes(scene, ids) }
  if (includeOrder) delta.order = [...scene.order]
  return delta
}

/** True when the two sides describe the same state, so the entry is a no-op. */
export function deltaEqual(a: SceneDelta, b: SceneDelta): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function invert(entry: HistoryEntry): HistoryEntry {
  return { ...entry, before: entry.after, after: entry.before }
}

export function capStack(stack: HistoryEntry[]): HistoryEntry[] {
  return stack.length > HISTORY_LIMIT ? stack.slice(stack.length - HISTORY_LIMIT) : stack
}

/**
 * Write a delta into a scene store. Used only by undo/redo — live edits take the
 * fine-grained path so that dragging one node touches one property.
 */
export function applyDelta(setScene: SetStoreFunction<Scene>, delta: SceneDelta): void {
  setScene(
    produce((s) => {
      for (const id of Object.keys(delta.nodes)) {
        const node = delta.nodes[id]!
        if (node === null) delete s.nodes[id]
        else s.nodes[id] = structuredClone(node)
      }
      if (delta.order) {
        // Drop ids the delta deleted, so order can never reference a missing node.
        s.order = delta.order.filter((id) => s.nodes[id] !== undefined)
      }
    }),
  )
}
